import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useMutation, useQueries } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useIntercom } from "react-use-intercom";
import { v4 as uuidv4 } from "uuid";
import { boomHelpApi } from "api/boom-help/api";
import { streamBoomHelpReply } from "api/boom-help/stream";
import { recordSupportContact } from "api/support-contacts";
import type {
  BoomHelpFeedbackReason,
  BoomHelpHandoffReason,
  BoomHelpRating
} from "api/boom-help/types";
import { getErrorMessage } from "utils/error";
import { useBoomHelpStore, type BoomHelpEntry } from "./store";
import {
  BOOM_HELP_COPY,
  BOOM_HELP_POLL_INTERVAL_MS,
  BOOM_HELP_POLL_TIMEOUT_MS
} from "./constants";

export const useSendBoomHelpMessage = () => {
  const location = useLocation();
  const { conversationId, startConversation, addMessages } = useBoomHelpStore();

  return useMutation({
    mutationKey: ["BOOM_HELP", "MESSAGES", "CREATE"],
    mutationFn: (content: string) =>
      boomHelpApi.sendMessage({
        conversation_id: conversationId ?? undefined,
        content,
        page: `${location.pathname}${location.search}`,
        client_message_id: uuidv4()
      }),
    onSuccess: (data) => {
      if (data.conversation_id !== conversationId)
        startConversation(data.conversation_id);
      addMessages([data.user_message, data.assistant_message]);
    },
    onError: (error) => toast.error(getErrorMessage(error))
  });
};

const isInFlight = (message: BoomHelpEntry) =>
  message.status === "pending" || message.status === "streaming";

const useReplyStreams = (
  inFlight: BoomHelpEntry[],
  onFallback: (messageId: number) => void
) => {
  const { updateMessage } = useBoomHelpStore();
  const streams = useRef(new Map<number, AbortController>());

  useEffect(() => {
    const active = new Set(inFlight.map((message) => message.id));
    streams.current.forEach((controller, id) => {
      if (active.has(id)) return;
      controller.abort();
      streams.current.delete(id);
    });
    inFlight
      .filter((message) => !streams.current.has(message.id))
      .forEach((message) => {
        const controller = new AbortController();
        streams.current.set(message.id, controller);
        streamBoomHelpReply({
          conversationId: message.conversation_id,
          messageId: message.id,
          signal: controller.signal,
          onMessage: (update) => updateMessage(message.id, update)
        }).catch(() => {
          if (!controller.signal.aborted) onFallback(message.id);
        });
      });
  }, [inFlight, updateMessage, onFallback]);

  useEffect(
    () => () => {
      streams.current.forEach((controller) => controller.abort());
      streams.current.clear();
    },
    []
  );
};

export const usePendingReplies = () => {
  const { messages, updateMessage } = useBoomHelpStore();
  const [fallbackIds, setFallbackIds] = useState<number[]>([]);
  const inFlight = useMemo(() => messages.filter(isInFlight), [messages]);
  const onFallback = useCallback(
    (messageId: number) => setFallbackIds((ids) => [...ids, messageId]),
    []
  );
  useReplyStreams(inFlight, onFallback);

  const polled = inFlight.filter((message) => fallbackIds.includes(message.id));
  const results = useQueries({
    queries: polled.map((message) => ({
      queryKey: ["BOOM_HELP", "MESSAGE", message.conversation_id, message.id],
      queryFn: () =>
        boomHelpApi.getMessage({
          conversationId: message.conversation_id,
          messageId: message.id
        }),
      refetchInterval: BOOM_HELP_POLL_INTERVAL_MS,
      refetchIntervalInBackground: true
    }))
  });

  useEffect(() => {
    results.forEach((result, index) => {
      const message = polled[index];
      const data = result.data;
      if (!message || !data) return;
      if (data.status !== message.status || data.content !== message.content)
        updateMessage(message.id, data);
    });
  }, [results, polled, updateMessage]);

  useEffect(() => {
    const timer = setInterval(() => {
      inFlight
        .filter(
          (message) =>
            Date.now() - new Date(message.created_at).getTime() >
            BOOM_HELP_POLL_TIMEOUT_MS
        )
        .forEach((message) => updateMessage(message.id, { status: "failed" }));
    }, BOOM_HELP_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [inFlight, updateMessage]);
};

export const useRateBoomHelpMessage = () => {
  const { rate } = useBoomHelpStore();

  return useMutation({
    mutationKey: ["BOOM_HELP", "FEEDBACK", "UPDATE"],
    mutationFn: (variables: {
      messageId: number;
      rating: BoomHelpRating;
      reason?: BoomHelpFeedbackReason;
      comment?: string;
    }) => boomHelpApi.rateMessage(variables),
    onMutate: ({ messageId, rating, reason }) => {
      if (!reason) rate(messageId, rating);
    },
    onError: (error) => toast.error(getErrorMessage(error))
  });
};

export const useHandOffToSupport = () => {
  const { conversationId, messages, markHandedOff, close } = useBoomHelpStore();
  const { show, showConversation, showNewMessage } = useIntercom();

  return useMutation({
    mutationKey: ["BOOM_HELP", "HANDOFF", "CREATE"],
    mutationFn: async (reason: BoomHelpHandoffReason) => {
      if (conversationId === null) return null;
      return boomHelpApi.handOff({ conversationId, reason });
    },
    onSuccess: (data) => {
      const lastQuestion = [...messages]
        .reverse()
        .find((message) => message.role === "user")?.content;
      close();
      if (data?.delivered && data.intercom_conversation_id) {
        showConversation(Number(data.intercom_conversation_id));
        markHandedOff();
        return;
      }
      void recordSupportContact("boom_help_fallback");
      if (lastQuestion) showNewMessage(lastQuestion);
      else show();
      if (data) toast.info(BOOM_HELP_COPY.handoffNotSent);
    },
    onError: (error) => toast.error(getErrorMessage(error))
  });
};
